import { ref, reactive, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { event } from 'vue-gtag'
import * as Sentry from '@sentry/vue'
import { useCartStore } from '@/stores/cart'
import { useAuthStore } from '@/stores/auth'
import { ordersAPI } from '@/api/modules/orders'
import { paymentsAPI } from '@/api/modules/payments'
import { shippingAPI } from '@/api/modules/shipping'
import { usersAPI } from '@/api/modules/users.js'

const FORM_KEY = 'checkout_personal_info'
const CHOICE_KEY = 'checkout_shipping_payment'

const priceFormat = new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' })
export const formatEur = (value) => priceFormat.format(Number(value) || 0)

export const COUNTRIES = [
  { code: 'SK', name: 'Slovensko' },
  { code: 'CZ', name: 'Česko' },
  { code: 'HU', name: 'Maďarsko' },
  { code: 'AT', name: 'Rakúsko' },
  { code: 'PL', name: 'Poľsko' },
]

/** A cart item as the cart and the summary show it (products, product sets and own spice mixes) */
export const cartLine = (item) => {
  const quantity = Number(item?.quantity) || 0
  const total = Number.isNaN(parseFloat(item?.total_price))
    ? parseFloat(item?.final_price ?? item?.unit_price ?? 0) * quantity
    : parseFloat(item.total_price)

  if (item?.is_custom_mix) {
    const mix = item.custom_mix || {}
    const count = mix.spices?.length || 0
    return {
      key: item.id,
      name: `Vlastná zmes „${mix.name || ''}“`,
      detail: [mix.package?.name, mix.meat_kg && `na ${mix.meat_kg} kg mäsa`, `${count} ${count >= 2 && count <= 4 ? 'korenia' : 'korení'}`]
        .filter(Boolean).join(' · '),
      image: mix.package?.image,
      slug: null,
      unitPrice: parseFloat(item.unit_price || 0),
      total,
      quantity,
      mix,
    }
  }
  if (item?.is_set_item) {
    return {
      key: item.id,
      name: item.set_metadata?.name || 'Balíček',
      detail: (item.set_metadata?.items || []).map((i) => i.name || i.product_name).filter(Boolean).join(', '),
      image: item.set_metadata?.image,
      slug: null,
      unitPrice: parseFloat(item.unit_price || 0),
      total,
      quantity,
    }
  }
  const product = item?.product || {}
  return {
    key: item.id,
    name: product.name || 'Produkt',
    detail: product.variant_name || product.formatted_weight || '',
    image: product.main_image,
    slug: product.slug,
    unitPrice: parseFloat(item?.final_price ?? item?.unit_price ?? product.price ?? 0),
    total,
    quantity,
    productId: item.product_id,
    pickupOnly: !!product.pickup_only,
  }
}

/** A pie of the mix's spices in their colours, for lines without a photo */
/** Grams of a spice in an own mix: "200 g", "12,5 g" */
export const formatMixGrams = (grams) => `${String(Math.round(Number(grams) * 10) / 10).replace('.', ',')} g`

export const mixPieStyle = (mix) => {
  const spices = mix?.spices || []
  const total = spices.reduce((sum, spice) => sum + Number(spice.grams || 0), 0) || 1
  let from = 0
  const stops = spices.map((spice) => {
    const to = from + (Number(spice.grams || 0) / total) * 100
    const stop = `${spice.color} ${from}% ${to}%`
    from = to
    return stop
  })
  return { background: stops.length ? `conic-gradient(${stops.join(', ')})` : '#e2e8f0' }
}

const nameOrder = new Intl.Collator('sk', { sensitivity: 'base', numeric: true })

/** Cart items alphabetically, so adding or changing a line doesn't shuffle the list (the API returns them unordered) */
export const sortCartItems = (items) => [...(items || [])].sort((a, b) => {
  const la = cartLine(a)
  const lb = cartLine(b)
  return nameOrder.compare(la.name, lb.name) || nameOrder.compare(la.detail, lb.detail) || a.id - b.id
})

export const paymentFee = (method, amount) => {
  if (!method || method.has_fees === false) return 0
  return parseFloat(method.fee_fixed || 0) + (amount * parseFloat(method.fee_percentage || 0)) / 100
}

const blankAddress = () => ({ street: '', city: '', postal_code: '', country: 'SK' })

const readSession = (key) => {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null')
  } catch {
    return null
  }
}

const gtagItems = (items) => items.map((item, index) => ({
  item_id: item.is_set_item ? item.set_metadata?.set_id : (item.product?.sku || item.product?.id),
  item_name: item.is_set_item ? item.set_metadata?.set_name : (item.product?.name || 'Unknown Product'),
  price: item.unit_price,
  quantity: item.quantity,
  index,
}))

/**
 * State of the one-page checkout: contact and address form, shipping and
 * payment choice, totals, validation and placing the order. The form is kept
 * in sessionStorage, so a refresh or a trip back to the cart keeps it.
 */
export function useCheckout() {
  const cartStore = useCartStore()
  const authStore = useAuthStore()
  const router = useRouter()

  const user = authStore.user || {}
  const saved = readSession(FORM_KEY)
  // Older sessions stored the shared checkout's form (billing_address.street, ...)
  const savedBilling = saved?.billing_address || saved?.address

  const form = reactive({
    first_name: saved?.first_name ?? user.first_name ?? '',
    last_name: saved?.last_name ?? user.last_name ?? '',
    email: saved?.email ?? user.email ?? '',
    phone: saved?.phone ?? user.phone ?? '',
    is_company: saved?.is_company ?? false,
    company: saved?.company ?? saved?.company_name ?? '',
    address: { ...blankAddress(), ...(savedBilling || {}) },
    different_shipping: saved?.different_shipping ?? false,
    shipping: { ...blankAddress(), ...(saved?.shipping || {}) },
    notes: saved?.notes ?? '',
  })

  const savedChoice = readSession(CHOICE_KEY)
  const allShippingMethods = ref([])
  // "Iba osobný odber" products in the cart: store pickup is the only shipping offered (the backend checks it too)
  const pickupOnlyItems = computed(() => cartStore.items.filter((item) => item.product?.pickup_only))
  const shippingMethods = computed(() => (pickupOnlyItems.value.length
    ? allShippingMethods.value.filter((m) => m.type === 'pickup')
    : allShippingMethods.value))
  const paymentMethods = ref([])
  const shippingId = ref(savedChoice?.shipping_method_id ?? null)
  const paymentId = ref(savedChoice?.payment_method_id ?? null)
  const pickupPoint = ref(savedChoice?.pickup_point ?? null)
  const acceptedTerms = ref(false)
  const loadingMethods = ref(true)

  const addresses = ref([])
  const errors = reactive({})
  const generalError = ref('')
  const submitting = ref(false)

  // --- Shipping and payment ------------------------------------------------

  const shipping = computed(() => shippingMethods.value.find((m) => m.id === shippingId.value) || null)
  const availablePayments = computed(() => {
    const disallowed = shipping.value?.disallowed_payment_methods || []
    return paymentMethods.value.filter((m) => !disallowed.includes(m.id))
  })
  const payment = computed(() => availablePayments.value.find((m) => m.id === paymentId.value) || null)

  const isStorePickup = computed(() => shipping.value?.type === 'pickup')
  const isPostaPickup = computed(() => shipping.value?.type === 'slovenska_posta')

  const shippingCost = (method) => cartStore.calculateShippingCost(method)
  const subtotal = computed(() => cartStore.subtotal)
  const discount = computed(() => cartStore.discountAmount)
  const shippingPrice = computed(() => (shipping.value ? shippingCost(shipping.value) : 0))
  const paymentPrice = computed(() => paymentFee(payment.value, cartStore.total))
  const total = computed(() => cartStore.total + shippingPrice.value + paymentPrice.value)

  // A shipping method no longer offered (a pickup only product came into the cart) gives way to the first offered one
  watch(shippingMethods, (list) => {
    if (!loadingMethods.value && !list.some((m) => m.id === shippingId.value)) shippingId.value = list[0]?.id ?? null
  })

  // A payment the new shipping method doesn't allow is replaced by the first allowed one
  watch(availablePayments, (list) => {
    if (!list.some((m) => m.id === paymentId.value)) paymentId.value = list[0]?.id ?? null
  })

  const loadMethods = async () => {
    loadingMethods.value = true
    try {
      const [shippingResponse, paymentResponse] = await Promise.all([
        shippingAPI.getShippingMethods(),
        paymentsAPI.getPaymentMethods(),
      ])
      allShippingMethods.value = shippingResponse.data || []
      paymentMethods.value = paymentResponse.data || []
      if (!shipping.value) shippingId.value = shippingMethods.value[0]?.id ?? null
      if (!payment.value) paymentId.value = availablePayments.value[0]?.id ?? null
    } catch (err) {
      console.error('Failed to load shipping/payment methods:', err)
      generalError.value = 'Nepodarilo sa načítať spôsoby dopravy a platby. Obnovte stránku, prosím.'
    } finally {
      loadingMethods.value = false
    }
  }

  // --- Saved addresses of signed-in customers --------------------------------

  const formatSaved = (address) => ({
    street: [address.address_line_1, address.address_line_2].filter(Boolean).join(', '),
    city: address.city || '',
    postal_code: address.postal_code || '',
    country: address.country || 'SK',
  })

  const useSavedAddress = (address, target = 'address') => {
    Object.assign(form[target], formatSaved(address))
    for (const field of ['street', 'city', 'postal_code']) delete errors[`${target}.${field}`]
  }

  const loadAddresses = async () => {
    if (!authStore.isAuthenticated) return
    try {
      const response = await usersAPI.getAddresses()
      addresses.value = response?.addresses || []
      const preferred = addresses.value.find((a) => a.is_default) || addresses.value[0]
      if (preferred && !form.address.street) useSavedAddress(preferred)
    } catch (err) {
      console.error('Failed to load addresses:', err)
    }
  }

  // --- Persistence -----------------------------------------------------------

  watch(form, () => sessionStorage.setItem(FORM_KEY, JSON.stringify(form)), { deep: true })
  watch([shippingId, paymentId, pickupPoint], () => {
    sessionStorage.setItem(CHOICE_KEY, JSON.stringify({
      shipping_method_id: shippingId.value,
      payment_method_id: paymentId.value,
      pickup_point: pickupPoint.value,
    }))
  })

  // --- Validation ------------------------------------------------------------

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
  const PHONE = /^\+?[0-9][0-9 ()/-]{7,18}$/
  const ZIP = { SK: /^\d{3}\s?\d{2}$/, CZ: /^\d{3}\s?\d{2}$/, HU: /^\d{4}$/, AT: /^\d{4}$/, PL: /^\d{2}-?\d{3}$/ }

  const addressErrors = (address, prefix) => {
    const result = {}
    if (!address.street.trim()) result[`${prefix}.street`] = 'Zadajte ulicu a číslo domu'
    else if (!/\d/.test(address.street)) result[`${prefix}.street`] = 'Doplňte aj číslo domu'
    if (!address.city.trim()) result[`${prefix}.city`] = 'Zadajte mesto alebo obec'
    const zip = ZIP[address.country]
    if (!address.postal_code.trim()) result[`${prefix}.postal_code`] = 'Zadajte PSČ'
    else if (zip && !zip.test(address.postal_code.trim())) result[`${prefix}.postal_code`] = 'PSČ nemá správny tvar'
    return result
  }

  const validate = () => {
    const result = {}
    if (!form.first_name.trim()) result.first_name = 'Zadajte meno'
    if (!form.last_name.trim()) result.last_name = 'Zadajte priezvisko'
    if (!EMAIL.test(form.email.trim())) result.email = form.email.trim() ? 'E-mail nemá správny tvar' : 'Zadajte e-mail'
    if (!PHONE.test(form.phone.trim())) result.phone = form.phone.trim() ? 'Telefón nemá správny tvar' : 'Zadajte telefón, kuriér vás bude kontaktovať'
    if (form.is_company && !form.company.trim()) result.company = 'Zadajte názov firmy'
    Object.assign(result, addressErrors(form.address, 'address'))
    if (form.different_shipping && !isStorePickup.value && !isPostaPickup.value) {
      Object.assign(result, addressErrors(form.shipping, 'shipping'))
    }
    if (!shipping.value) result.shipping = 'Vyberte spôsob dopravy'
    if (isPostaPickup.value && !pickupPoint.value) result.shipping = 'Vyberte výdajné miesto'
    if (!payment.value) result.payment = 'Vyberte spôsob platby'
    if (!acceptedTerms.value) result.terms = 'Na dokončenie objednávky potrebujeme váš súhlas'

    for (const key of Object.keys(errors)) delete errors[key]
    Object.assign(errors, result)
    return Object.keys(result).length === 0
  }

  // Re-check a field once the customer fixes it
  const clearError = (key) => { delete errors[key] }

  // Backend field => form field (422 responses)
  const serverField = (key) => {
    const [group, field] = key.split('.')
    const target = group === 'shipping_address' && form.different_shipping ? 'shipping' : 'address'
    if (group === 'shipping_method_id') return 'shipping'
    if (group === 'payment_method_id') return 'payment'
    if (field === 'name') return 'first_name'
    if (field === 'email' || field === 'phone') return field
    if (field === 'address_line_1') return `${target}.street`
    if (['city', 'postal_code', 'country'].includes(field)) return `${target}.${field}`
    return null
  }

  // --- Order -----------------------------------------------------------------

  const addressPayload = (address) => ({
    name: `${form.first_name.trim()} ${form.last_name.trim()}`,
    email: form.email.trim(),
    phone: form.phone.trim() || null,
    company: form.is_company ? form.company.trim() || null : null,
    address_line_1: address.street.trim(),
    address_line_2: null,
    city: address.city.trim(),
    postal_code: address.postal_code.trim(),
    country: address.country,
  })

  const orderPayload = () => {
    const billing = addressPayload(form.address)
    let shippingAddress = billing
    if (isPostaPickup.value && pickupPoint.value) {
      const point = pickupPoint.value
      shippingAddress = {
        ...billing,
        address_line_1: point.deliveryAddress?.street,
        address_line_2: point.label,
        city: point.deliveryAddress?.city,
        postal_code: point.deliveryAddress?.zip,
        pickup_point_id: point.id,
        pickup_point_kind: point.kind,
        pickup_point_label: point.label,
        pickup_point_routing: point.routing,
      }
    } else if (form.different_shipping && !isStorePickup.value) {
      shippingAddress = addressPayload(form.shipping)
    }

    return {
      shipping_method_id: shippingId.value,
      payment_method_id: paymentId.value,
      billing_address: billing,
      shipping_address: shippingAddress,
      notes: form.notes.trim(),
    }
  }

  const finish = () => {
    sessionStorage.removeItem(FORM_KEY)
    sessionStorage.removeItem(CHOICE_KEY)
    cartStore.clearCart()
  }

  /** Places the order; false when the form needs fixing (errors are set) */
  const submit = async () => {
    generalError.value = ''
    if (submitting.value || !validate()) return false

    submitting.value = true
    const items = gtagItems(cartStore.items)
    event('add_shipping_info', { currency: 'EUR', value: cartStore.total, shipping_tier: shipping.value?.name, items })
    event('add_payment_info', { currency: 'EUR', value: cartStore.total, payment_type: payment.value?.type, items })

    try {
      const response = await ordersAPI.createOrder(orderPayload())
      const orderNumber = response?.order?.order_number
      if (!orderNumber) throw new Error('Neplatná odpoveď servera')

      // The thank-you page shows guest orders by e-mail
      sessionStorage.setItem('guest_order_email', form.email.trim())

      if (payment.value?.type === 'mollie') {
        try {
          const mollie = await paymentsAPI.createMolliePayment(orderNumber)
          if (mollie?.checkout_url) {
            finish()
            window.location.href = mollie.checkout_url
            return true
          }
        } catch (mollieError) {
          Sentry.logger.error('Mollie payment creation failed', {
            order_number: orderNumber,
            error: mollieError.response?.data?.message || mollieError.message,
          })
        }
      }

      finish()
      router.push(`/checkout/thank-you/${orderNumber}`)
      return true
    } catch (err) {
      const fieldErrors = err.response?.status === 422 ? err.response.data?.errors : null
      if (fieldErrors) {
        for (const [key, messages] of Object.entries(fieldErrors)) {
          const field = serverField(key)
          if (field) errors[field] = Array.isArray(messages) ? messages[0] : String(messages)
        }
      }
      generalError.value = err.response?.data?.message && !fieldErrors
        ? err.response.data.message
        : fieldErrors
          ? 'Skontrolujte, prosím, zvýraznené údaje.'
          : 'Objednávku sa nepodarilo odoslať. Skúste to znova alebo nám zavolajte.'
      Sentry.logger.error('Order submission failed', {
        error: err.response?.data?.message || err.message,
        status_code: err.response?.status,
      })
      return false
    } finally {
      submitting.value = false
    }
  }

  return {
    form, errors, generalError, submitting, acceptedTerms,
    shippingMethods, pickupOnlyItems, paymentMethods, availablePayments, shippingId, paymentId, pickupPoint, loadingMethods,
    shipping, payment, isStorePickup, isPostaPickup,
    subtotal, discount, shippingPrice, paymentPrice, total, shippingCost,
    addresses, useSavedAddress,
    loadMethods, loadAddresses, validate, clearError, submit,
  }
}
